import React, { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../../lib/adminApi';
import type { ApiAdminNotification } from '../../types/admin';
import { Button } from '../common/Button';
import { toast } from 'sonner';

export const AdminNotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<ApiAdminNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { notifications: list } = await adminApi.listNotifications();
      setNotifications(list);
    } catch (err) {
      console.error('AdminNotifications: erro ao listar', err);
      setError('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkRead = async (notification: ApiAdminNotification) => {
    try {
      await adminApi.markNotificationRead(notification.id);
      setNotifications(prev => prev.map(n => (n.id === notification.id ? { ...n, isRead: true } : n)));
    } catch (err) {
      toast.error('Erro ao marcar como lida');
      console.error('AdminNotifications: markRead falhou', err);
    }
  };

  const unread = notifications.filter(n => !n.isRead);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-purple-200">
        Notificações <span className="text-sm text-purple-200/70">({unread.length} não lidas)</span>
      </h2>

      {loading ? (
        <p className="text-purple-200/70 py-4">Carregando…</p>
      ) : error ? (
        <p className="text-red-400 py-4">{error}</p>
      ) : (
        <div className="space-y-3">
          {notifications.length === 0 && (
            <p className="text-purple-200/70 py-4 text-center">Nenhuma notificação</p>
          )}
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`rounded-lg p-4 border ${
                notification.isRead
                  ? 'bg-white/5 border-transparent'
                  : 'bg-purple-500/10 border-purple-500/40'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-purple-200 font-medium">{notification.title}</p>
                  <p className="text-sm text-purple-200/70">{notification.message}</p>
                  <p className="text-xs text-purple-200/50 mt-1">
                    {new Date(notification.createdAt).toLocaleString('pt-BR')} · {notification.type}
                  </p>
                </div>
                {!notification.isRead && (
                  <Button size="sm" variant="outline" onClick={() => handleMarkRead(notification)}>
                    Marcar lida
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};